import React, { useState } from 'react';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { farmerPartnerWaitlistAPI } from '../../services/portalAPI';

/**
 * Farmhand → Farmer's Partner "coming soon" landing page.
 *
 * Replaces the live Farmhand experience (Quick Voice Log, Plans, Laura,
 * Egg Watch, Farm Map, etc.) while we rebuild as Farmer's Partner for a
 * February 2027 launch. Backend data + endpoints stay intact — this is a
 * frontend pause, not a teardown.
 */

const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const PortalFarmhandComingSoon: React.FC = () => {
  const { user } = usePortalAuth();
  const firstName = user?.name?.split(' ')[0] || 'Farmer';

  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyOnList, setAlreadyOnList] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await farmerPartnerWaitlistAPI.signup(email.trim());
      setSubmitted(true);
      setAlreadyOnList(Boolean(res.data?.already_subscribed));
      setMessage(res.data?.message || "You're on the list!");
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.page} data-testid="farmhand-coming-soon">
      {/* Welcome banner — same greeting the old Farmhand dashboard used */}
      <div style={styles.welcomeBanner}>
        <h1 style={styles.welcomeText}>
          {getGreeting()}, {firstName}
        </h1>
      </div>

      <div style={styles.card}>
        <h2 style={styles.headline}>
          FARMHAND IS BECOMING<br />FARMER'S PARTNER
        </h2>
        <p style={styles.subhead}>
          We're rebuilding from the ground up to serve you better.
          Relaunching February 2027.
        </p>

        <div style={styles.imageWrap}>
          <img
            src="/jeff-mattocks-apppa-award.png"
            alt="Jeff Mattocks receiving the APPPA Lifetime Achievement Award for his contributions to pastured poultry nutrition"
            style={{
              maxWidth: '400px',
              width: '100%',
              height: 'auto',
              display: 'block',
              margin: '0 auto',
            }}
          />
        </div>

        <p style={styles.body}>
          Laura is being rebuilt with the wisdom of Jeff Mattocks — author of{' '}
          <em>Niche Poultry Feeding and Management</em>, with over 40 years of
          experience in pastured poultry nutrition. We're honored to have his
          knowledge embedded in everything Farmer's Partner will offer.
        </p>

        <div style={styles.comingSection}>
          <h3 style={styles.sectionTitle}>WHAT'S COMING</h3>
          <ul style={styles.list}>
            <li>Voice-first farm management</li>
            <li>Trip-level planning that knows your animals</li>
            <li>Mapbox-powered pasture rotation tracking</li>
            <li>Integrated feed ordering through Red Bridge</li>
            <li>Production tracking and forecasting</li>
          </ul>
        </div>

        {/* Waitlist signup */}
        <div style={styles.waitlistBox}>
          <h3 style={styles.waitlistTitle}>GET LAUNCH NOTIFICATIONS</h3>
          {submitted ? (
            <div style={styles.successBox} data-testid="waitlist-success">
              <div style={styles.successIcon}>✓</div>
              <p style={styles.successText}>{message}</p>
            </div>
          ) : (
            <>
              <p style={styles.waitlistText}>
                We'll let you know the moment Farmer's Partner is ready.
              </p>
              <form onSubmit={handleSubmit} style={styles.form}>
                {error && <div style={styles.error}>{error}</div>}
                <label style={styles.label} htmlFor="waitlist-email">Email</label>
                <input
                  id="waitlist-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                  placeholder="you@example.com"
                  required
                  disabled={submitting}
                  autoComplete="email"
                />
                <button type="submit" style={styles.submitButton} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Notify Me at Launch'}
                </button>
              </form>
            </>
          )}
        </div>

        <p style={styles.contact}>
          Questions or input on what to build next? Email{' '}
          <a href="mailto:brad@theredbridgefarm.com" style={styles.contactLink}>
            brad@theredbridgefarm.com
          </a>{' '}
          or call{' '}
          <a href="tel:509-738-9004" style={styles.contactLink}>509-738-9004</a>.
        </p>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    maxWidth: '760px',
    margin: '0 auto',
    padding: '1rem',
  },
  welcomeBanner: {
    marginBottom: '1rem',
  },
  welcomeText: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1a4d1a',
    margin: 0,
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '2rem 1.75rem',
    textAlign: 'center',
  },
  headline: {
    fontSize: '1.9rem',
    fontWeight: 800,
    color: '#722F37',
    letterSpacing: '1px',
    lineHeight: 1.2,
    margin: '0 0 1rem',
  },
  subhead: {
    fontSize: '1.05rem',
    color: '#444',
    margin: '0 0 1.5rem',
    lineHeight: 1.5,
  },
  imageWrap: {
    margin: '1.5rem 0',
  },
  body: {
    fontSize: '1rem',
    color: '#333',
    lineHeight: 1.7,
    textAlign: 'left',
    margin: '1.5rem 0',
  },
  comingSection: {
    textAlign: 'left',
    background: '#f7faf5',
    borderRadius: '12px',
    padding: '1.25rem 1.5rem',
    margin: '1.5rem 0',
  },
  sectionTitle: {
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '1.5px',
    color: '#1a4d1a',
    margin: '0 0 0.75rem',
  },
  list: {
    margin: 0,
    paddingLeft: '1.25rem',
    color: '#333',
    lineHeight: 1.9,
  },
  waitlistBox: {
    background: 'linear-gradient(135deg, #1a4d1a 0%, #2e7d32 100%)',
    borderRadius: '12px',
    padding: '1.5rem',
    margin: '1.5rem 0',
    color: '#ffffff',
  },
  waitlistTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    letterSpacing: '1.5px',
    margin: '0 0 0.5rem',
  },
  waitlistText: {
    fontSize: '0.95rem',
    margin: '0 0 1rem',
    opacity: 0.95,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    textAlign: 'left',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  input: {
    padding: '0.75rem',
    fontSize: '16px',
    border: 'none',
    borderRadius: '8px',
    outline: 'none',
    minHeight: '44px',
  },
  submitButton: {
    background: '#ffffff',
    color: '#1a4d1a',
    border: 'none',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: 700,
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '0.5rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  error: {
    background: 'rgba(255,255,255,0.95)',
    color: '#c62828',
    padding: '0.6rem 0.8rem',
    borderRadius: '6px',
    fontSize: '0.85rem',
  },
  successBox: {
    textAlign: 'center',
    padding: '0.5rem 0',
  },
  successIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#ffffff',
    color: '#1a4d1a',
    fontSize: '1.6rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 0.75rem',
  },
  successText: {
    fontSize: '1rem',
    margin: 0,
    lineHeight: 1.5,
  },
  contact: {
    fontSize: '0.9rem',
    color: '#666',
    margin: '1rem 0 0',
    lineHeight: 1.6,
  },
  contactLink: {
    color: '#722F37',
    textDecoration: 'none',
    fontWeight: 600,
  },
};

export default PortalFarmhandComingSoon;
